import React, { FC, Fragment } from 'react';
import { Box, FormControl, MultiSelect, Text, TextFieldAppearance, TextFieldSize } from '@bechtel/gds';
import { AuthRole, User, WorkPackageDiscipline } from '@bechtel/types';
import { NetworkStatus, useQuery } from '@apollo/client';
import { ErrorMessage, Field } from 'formik';
import { filter, get } from 'lodash';
import { ErrorRetry, useAppUser, ProjectAppTypes, useEnvProvider } from '@bechtel/client-common';
import { GET_PROJECT_WORK_PACKAGE_DISCIPLINES_WITH_STATUS } from '@bechtel/client-gql';
import { useRoleQuery } from '~/user-admin/hooks/use-role';
import { SepartorStyled } from '../styled';
import CompanyList from './company-list';
import FacilityList from './facility-list';
import { FieldError, TextFieldStyled } from './styled';
import { UserDetailItemProps } from './types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { FormikState } from '~/user-admin/users/form-state';
import { FormikProps } from 'formik';

interface UserInfoProps {
  isSupervisor: boolean;
  formikProps: FormikProps<FormikState>;
  user: User | null | undefined;
  isProjectUser?: boolean;
  isEditUser?: boolean;
}

const FormControlLabel = ({ label, disabled }) => {
  const { i18n } = useLingui();
  return (
    <Box css={disabled ? 'opacity:0.5' : ''}>
      <Text family="graphik" color="bechtel.blue.800" size="300">
        {i18n._(label)}
      </Text>
    </Box>
  );
};

const Item = ({ item }: any) => {
  return (
    <Text size="400" color="grey.1000">
      {item.name}
    </Text>
  );
};

const UserInfo: FC<UserInfoProps> = ({ isSupervisor, user, isProjectUser, isEditUser }) => {
  return (
    <Fragment>
      <Box mb={{ sm: '600' }}>
        <FormControl
          style={{ flexGrow: 1 }}
          label={<FormControlLabel label={t`Last Name`} disabled={isProjectUser !== false} />}
        >
          {isSupervisor ? (
            <TextFieldStyled disabled={isProjectUser !== false} defaultValue={user?.lastName ? user.lastName : ''} />
          ) : (
            <Field name="lastName">
              {({ field }) => {
                return <TextFieldStyled disabled={isProjectUser !== false} {...field} />;
              }}
            </Field>
          )}
        </FormControl>
      </Box>
      <Box mb={{ sm: '600' }}>
        <FormControl
          style={{ flexGrow: 1 }}
          label={<FormControlLabel label={t`First Name`} disabled={isProjectUser !== false} />}
        >
          {isSupervisor ? (
            <TextFieldStyled disabled={isProjectUser !== false} defaultValue={user?.firstName ? user.firstName : ''} />
          ) : (
            <Field name="firstName">
              {({ field }) => {
                return <TextFieldStyled disabled={isProjectUser !== false} {...field} />;
              }}
            </Field>
          )}
        </FormControl>
      </Box>
      {isProjectUser !== false && (
        <Box mb={{ sm: '600' }}>
          <Box flexBasis={{ sm: '42%' }} mr={{ sm: '500' }}>
            <FormControl
              style={{ flexGrow: 1 }}
              label={
                <FormControlLabel
                  label={t`BUN ${!isSupervisor && !isEditUser ? '(Required)' : ''}`}
                  disabled={isSupervisor || isEditUser}
                />
              }
            >
              {isSupervisor ? (
                <TextFieldStyled disabled={true} defaultValue={user?.bun ? user.bun : ''} />
              ) : (
                <Field name="bun">
                  {({ field }) => {
                    return (
                      <div>
                        <TextFieldStyled disabled={isEditUser} {...field} defaultValue={user?.bun ? user.bun : ''} />
                      </div>
                    );
                  }}
                </Field>
              )}
            </FormControl>
          </Box>

          <FormControl style={{ flexGrow: 1 }} label={<FormControlLabel label={t`Other ID`} disabled={true} />}>
            {isSupervisor ? (
              <TextFieldStyled disabled={true} defaultValue={user?.etrackId ? user.etrackId : ''} />
            ) : (
              <Field name="lobEmployeeId">
                {({ field }) => {
                  return <TextFieldStyled disabled={true} {...field} />;
                }}
              </Field>
            )}
          </FormControl>
        </Box>
      )}

      {isSupervisor === false && (
        <Box mb={{ sm: '400' }}>
          <FormControl
            style={{ flexGrow: 1 }}
            label={<FormControlLabel label={t`BECIMS ID (Required)`} disabled={false} />}
          >
            <Field name="becimsId">
              {({ field }) => {
                return (
                  <div>
                    <TextFieldStyled {...field} defaultValue={user?.becImsId ? user.becImsId : ''} />
                  </div>
                );
              }}
            </Field>
          </FormControl>
        </Box>
      )}
    </Fragment>
  );
};

export const UserDetailItem: FC<UserDetailItemProps> = ({ user, formikProps, isProjectUser, isEditUser }) => {
  const { envConfig } = useEnvProvider();
  const { setFieldValue, values } = formikProps;
  const { user: loggedInUser } = useAppUser();
  const {
    loading: roleLoading,
    error: roleError,
    data: roleData,
    refetch: roleRefetch,
    networkStatus: roleNetworkStatus,
  } = useRoleQuery();
  const {
    loading: disciplineLoading,
    error: disciplineError,
    data: disciplineData,
    refetch: disciplineRefetch,
    networkStatus: disciplineNetworkStatus,
  } = useQuery(GET_PROJECT_WORK_PACKAGE_DISCIPLINES_WITH_STATUS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      id: loggedInUser?.project?.id,
    },
  });
  const roleList: AuthRole[] = get(roleData, 'roles', []);
  const wpDisciplineList: WorkPackageDiscipline[] = get(disciplineData, 'projectConfigById.workPackageDisciplines', []);
  const filteredWpDisciplineList: WorkPackageDiscipline[] = filter(
    wpDisciplineList,
    (discipline) => discipline.isActive
  );
  const isPFAppType = envConfig.appName === ProjectAppTypes.PF;
  return (
    <Box
      flexDirection={{ sm: 'column' }}
      pt={{ sm: '500' }}
      pb={{ sm: '700' }}
      px={{ sm: '600' }}
      flexGrow={{ sm: '1' }}
      style={{ overflowY: 'auto' }}
    >
      <Box flexDirection={{ sm: 'column' }}>
        <Box mb={{ sm: '600' }}>
          <Text size="400" color="grey.1000">
            <Trans>Name</Trans>
          </Text>
        </Box>
        <UserInfo
          isSupervisor={false}
          formikProps={formikProps}
          user={user}
          isProjectUser={isProjectUser}
          isEditUser={isEditUser}
        />
        {isProjectUser !== false && (
          <>
            <SepartorStyled mt={{ sm: '700' }} mb={{ sm: '550' }} />
            <Box pb={{ sm: '600' }} data-testid="user-detail-supervisor">
              <Text size="400" color="grey.1000">
                <Trans>Supervisor</Trans>
              </Text>
            </Box>
            <UserInfo
              isSupervisor={true}
              formikProps={formikProps}
              user={user?.supervisor}
              isProjectUser={isProjectUser}
            />
          </>
        )}
        <SepartorStyled mt={{ sm: '550' }} mb={{ sm: '600' }} />
        <Box pb={{ sm: '700' }}>
          <Text size="400" color="grey.1000">
            <Trans>Role</Trans>
          </Text>
        </Box>
        {!roleLoading && roleError && (
          <ErrorRetry refetch={roleRefetch}>
            <Trans>There was a problem loading role list. Please click the button below to try again.</Trans>
          </ErrorRetry>
        )}
        {!roleError && (
          <Field name="roles">
            {({ field }) => {
              return (
                <Box flexDirection={{ sm: 'column' }} mb={{ sm: '600' }}>
                  <FormControl
                    data-testid="user-detail-roles"
                    style={{ flexGrow: 1 }}
                    label={t`${isPFAppType ? 'PipeFighter' : 'ArcStryker'} Role (Required)`}
                  >
                    <MultiSelect
                      {...field}
                      items={roleList}
                      itemComponent={Item}
                      selectedItems={values.roles}
                      size={TextFieldSize.LARGE}
                      appearance={TextFieldAppearance.NORMAL}
                      displayKeyField="name"
                      loading={roleLoading || roleNetworkStatus === NetworkStatus.refetch}
                      onSelectedItemsChange={async (changes) => {
                        const { selectedItems } = changes;
                        setFieldValue('roles', selectedItems);
                      }}
                    ></MultiSelect>
                  </FormControl>
                  <ErrorMessage name="roles">
                    {(msg) => {
                      return (
                        <Box pt={{ sm: '400' }} pb={{ sm: '500' }}>
                          <FieldError>{msg}</FieldError>
                        </Box>
                      );
                    }}
                  </ErrorMessage>
                </Box>
              );
            }}
          </Field>
        )}
        <Box mb={{ sm: '600' }}>
          <FormControl
            style={{ flexGrow: 1 }}
            label={<FormControlLabel label={t`Job Title`} disabled={user && user.id.length > 0 ? true : false} />}
          >
            <Field name="jobTitle">
              {({ field }) => {
                return (
                  <TextFieldStyled
                    disabled={user && user.id.length > 0 ? true : false}
                    {...field}
                    placeholder={t`Job Title`}
                  />
                );
              }}
            </Field>
          </FormControl>
        </Box>

        {!disciplineLoading && disciplineError && (
          <ErrorRetry refetch={disciplineRefetch}>
            <Trans>There was a problem loading discipline list. Please click the button below to try again.</Trans>
          </ErrorRetry>
        )}
        {!disciplineError && (
          <Field name="disciplines">
            {({ field }) => {
              return (
                <Box data-testid="user-detail-disciplines" mb={{ sm: '600' }}>
                  <FormControl style={{ flexGrow: 1 }} label={t`Discipline`}>
                    <MultiSelect
                      {...field}
                      items={filteredWpDisciplineList}
                      itemComponent={Item}
                      selectedItems={values.disciplines}
                      size={TextFieldSize.LARGE}
                      appearance={TextFieldAppearance.NORMAL}
                      displayKeyField="name"
                      loading={disciplineLoading || disciplineNetworkStatus === NetworkStatus.refetch}
                      onSelectedItemsChange={async (changes) => {
                        const { selectedItems } = changes;
                        setFieldValue('disciplines', selectedItems);
                      }}
                    ></MultiSelect>
                  </FormControl>
                </Box>
              );
            }}
          </Field>
        )}
        <CompanyList formikProps={formikProps} />
        <SepartorStyled mt={{ sm: '700' }} mb={{ sm: '600' }} />
        <Box pb={{ sm: '700' }}>
          <Text size="400" color="grey.1000">
            <Trans>Facility Responsibility</Trans>
          </Text>
        </Box>
        <FacilityList formikProps={formikProps} />
      </Box>
    </Box>
  );
};

export default UserDetailItem;
